import { OverlayScrollArea } from '@odo/shared-ui';
import { Link } from 'react-router-dom';
import type { OverviewAssignee } from '../../../services/dmMonitoringOverview';
import { useUser } from '../../../providers/UserProvider';
import { OverviewCard } from '../../billing/overview/OverviewCard';
import { AssigneeDisplay } from '../../conversations/AssigneeDisplay';
import { CATEGORY_LEGEND } from './overviewLegend';
import { assigneeListPath } from './overviewLinks';
import { EmptyNote, StackedBar } from './StackedBar';

const ROW_GRID =
    'grid grid-cols-[minmax(0,2fr)_3.5rem_minmax(3rem,1.6fr)_5.5rem_4.5rem] items-center gap-3';
// Rows past this scroll inside the card, keeping it level with Backlog age beside it.
const TABLE_MAX_HEIGHT = 'max-h-[248px]';
const SCROLL_TRACK_INSET_PX = 4;
const UNASSIGNED_LABEL = 'Unassigned';
const UNKNOWN_PERSON = 'Unknown user';

function displayName(assignee: OverviewAssignee): string {
    if (assignee.userId === null) return UNASSIGNED_LABEL;
    return assignee.name ?? assignee.email ?? UNKNOWN_PERSON;
}

interface AssigneeRowProps {
    assignee: OverviewAssignee;
    maxItems: number;
    currentUserEmail: string | undefined;
}

function AssigneeRow({ assignee, maxItems, currentUserEmail }: AssigneeRowProps) {
    return (
        <Link
            to={assigneeListPath(assignee, currentUserEmail)}
            className={`${ROW_GRID} rounded-lg px-2 py-1.5 text-sm hover:bg-gray-50 transition-colors`}
        >
            <AssigneeDisplay
                name={assignee.userId === null ? null : displayName(assignee)}
                variant="avatar-name"
                unassignedLabel={UNASSIGNED_LABEL}
            />
            <span className="text-right tabular-nums text-gray-700">{assignee.patients}</span>
            <StackedBar
                label={displayName(assignee)}
                total={maxItems}
                heightClassName="h-2.5"
                segments={[
                    {
                        key: 'notification',
                        count: assignee.notifications,
                        ...CATEGORY_LEGEND.notification
                    },
                    { key: 'message', count: assignee.messages, ...CATEGORY_LEGEND.message }
                ]}
            />
            <span className="text-right font-semibold tabular-nums text-gray-900">
                {assignee.notifications}
            </span>
            <span className="text-right font-semibold tabular-nums text-gray-900">
                {assignee.messages}
            </span>
        </Link>
    );
}

export function AssigneeWorkloadCard({ assignees }: { assignees: readonly OverviewAssignee[] }) {
    const { user } = useUser();
    const maxItems = Math.max(0, ...assignees.map((a) => a.notifications + a.messages));
    return (
        <OverviewCard
            title="Workload by assignee"
            info="Patients with open items, by sticky assignee. Click a row to open its list."
        >
            {assignees.length === 0 ? (
                <EmptyNote>No open items.</EmptyNote>
            ) : (
                <OverlayScrollArea
                    axis="both"
                    className="-mx-2"
                    contentClassName={TABLE_MAX_HEIGHT}
                    trackInsetPx={SCROLL_TRACK_INSET_PX}
                >
                    <div className="flex min-w-[440px] flex-col gap-1">
                        <div
                            className={`${ROW_GRID} sticky top-0 z-[1] border-b border-gray-200 bg-white px-2 pb-2 text-xs font-semibold text-gray-500`}
                        >
                            <span>Assignee</span>
                            <span className="text-right">Patients</span>
                            <span />
                            <span className="text-right">Notifications</span>
                            <span className="text-right">Messages</span>
                        </div>
                        {assignees.map((assignee) => (
                            <AssigneeRow
                                key={assignee.userId ?? UNASSIGNED_LABEL}
                                assignee={assignee}
                                maxItems={maxItems}
                                currentUserEmail={user?.email}
                            />
                        ))}
                    </div>
                </OverlayScrollArea>
            )}
        </OverviewCard>
    );
}
